import { useTranslation } from 'react-i18next';
import type { BulkFieldChange, BulkRow } from '../api/types';
import { kindLabelKey } from './qrCodeFormParts';

const ROW_CLASS: Record<BulkRow['action'], string> = {
  Create: 'bulk-row--create',
  Update: 'bulk-row--update',
  Unchanged: 'bulk-row--unchanged',
  Error: 'bulk-row--error',
};

const BADGE_CLASS: Record<BulkRow['action'], string> = {
  Create: 'badge bulk-badge--create',
  Update: 'badge bulk-badge--update',
  Unchanged: 'badge badge-neutral',
  Error: 'badge bulk-badge--error',
};

function Change({ change, isNew }: { change: BulkFieldChange; isNew: boolean }) {
  const { t } = useTranslation();

  return (
    <li className="bulk-change">
      <span className="bulk-change__field">{t(`bulk.fields.${change.field}`, { defaultValue: change.field })}</span>
      {!isNew && (
        <>
          <del className="bulk-change__before">{change.before || t('bulk.empty')}</del>
          <i className="pi pi-arrow-right bulk-change__arrow" aria-hidden="true" />
        </>
      )}
      <mark className="bulk-change__after">{change.after || t('bulk.empty')}</mark>
    </li>
  );
}

export function BulkPreviewTable({ rows }: { rows: BulkRow[] }) {
  const { t } = useTranslation();

  return (
    <div className="table-responsive">
      <table className="table table-sm bulk-table align-middle">
        <thead>
          <tr>
            <th scope="col">{t('bulk.columns.row')}</th>
            <th scope="col">{t('bulk.columns.status')}</th>
            <th scope="col">{t('bulk.columns.code')}</th>
            <th scope="col">{t('bulk.columns.title')}</th>
            <th scope="col">{t('bulk.columns.details')}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.row} className={ROW_CLASS[row.action]} data-action={row.action}>
              <td className="mono">{row.row}</td>
              <td>
                <span className={BADGE_CLASS[row.action]}>{t(`bulk.actions.${row.action}`)}</span>
              </td>
              <td>
                {row.slug ? (
                  <span className="mono">{row.slug}</span>
                ) : row.action === 'Create' ? (
                  <span className="bulk-slug--generated">{t('bulk.generatedSlug')}</span>
                ) : (
                  <span className="bulk-slug--generated">-</span>
                )}
                {row.kind && (
                  <span className="small text-body-secondary d-block">
                    {t(kindLabelKey(row.kind))}
                  </span>
                )}
              </td>
              <td>{row.title}</td>
              <td>
                {row.changes.length > 0 && (
                  <ul className="bulk-changes">
                    {row.changes.map((change) => (
                      <Change key={change.field} change={change} isNew={row.action === 'Create'} />
                    ))}
                  </ul>
                )}
                {row.errors.map((error) => (
                  <p key={error} className="bulk-message bulk-message--error">
                    <i className="pi pi-times-circle" aria-hidden="true" />
                    {error}
                  </p>
                ))}
                {row.warnings.map((warning) => (
                  <p key={warning} className="bulk-message bulk-message--warning">
                    <i className="pi pi-exclamation-triangle" aria-hidden="true" />
                    {warning}
                  </p>
                ))}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
